/**
 * GIS Configuration - TPL Real Data Integration
 * Central configuration for map, layers, and services
 */

// Detect tenant for naming
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isNexus = hostname.includes('nexus');
const isLNG = hostname.includes('lng');

// Tenant-specific layer names
const layerNames = {
  tracts: isNexus ? (isLNG ? 'LNG Assets' : 'Land Assets') : 'TPL Land Tracts',
  wells: isNexus ? (isLNG ? 'Facilities' : 'Royalty Wells') : 'TPL Royalty Wells',
  contracts: isNexus ? (isLNG ? 'Contracts' : 'Contracts (Easements)') : 'TPL Contracts (Easements)',
  dsus: isNexus ? (isLNG ? 'Production Units' : 'Development Units (DSUs)') : 'Development Units (DSUs)',
  rigs: isNexus ? (isLNG ? 'Construction Sites' : 'Active Rigs') : 'Active Rigs',
};

export const mapConfig = {
  center: [
    parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || '31.7'),
    parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || '-102.9')
  ],
  zoom: parseInt(process.env.REACT_APP_MAP_ZOOM || '7'),
  minZoom: 3,
  maxZoom: 18
};

export const arcgisConfig = {
  apiKey: process.env.REACT_APP_ARCGIS_API_KEY,
  basemapStyle: 'arcgis/topographic',
};

/**
 * Feature Service Configuration - Real TPL Data
 */
export const featureServices = {
  // PRIMARY LAYERS (Default Visible)
  tracts: {
    name: layerNames.tracts,
    url: 'http://localhost:5001/api/geojson/tracts',
    geojsonFile: 'tp-assets-all.geojson',
    fields: {
      id: 'OBJECTID',
      tractId: 'TractID',
      tractType: 'TractType',
      county: 'CountyName',
      block: 'Block',
      section: 'Sec',
      acreage: 'TractAcres',
      contractId: 'ContractID',
      status: 'Status',
      orri: 'ORRI',
      nri: 'NRI',
      nma: 'NMA',
      nra: 'NRA',
      dealName: 'DealName',
      grantee: 'Grantee',
      displayName: 'TractID'
    },
    visible: true,
    defaultVisible: true,
    style: {
      fillColor: '#3388ff',
      fillOpacity: 0.3,
      color: '#2c3e50',
      weight: 2
    },
    popupTemplate: {
      title: 'Tract: {TractID}',
      content: [
        { fieldName: 'TractID', label: 'Tract ID' },
        { fieldName: 'TractType', label: 'Tract Type' },
        { fieldName: 'CountyName', label: 'County' },
        { fieldName: 'Block', label: 'Block' },
        { fieldName: 'Sec', label: 'Section' },
        { fieldName: 'TractAcres', label: 'Tract Acres' },
        { fieldName: 'Status', label: 'Status' },
        { fieldName: 'ORRI', label: 'ORRI' },
        { fieldName: 'NRI', label: 'NRI' },
        { fieldName: 'NMA', label: 'Net Mineral Acres' },
        { fieldName: 'DealName', label: 'Deal Name' },
        { fieldName: 'Grantee', label: 'Grantee' }
      ]
    }
  },

  wells: {
    name: layerNames.wells,
    url: 'http://localhost:5001/api/geojson/wells',
    geojsonFile: 'tp-royalty-wells.geojson',
    fields: {
      id: 'OBJECTID',
      api: 'API',
      api14: 'API14',
      wellName: 'WellName',
      wellPadId: 'WellPadID',
      activeNri: 'ActiveNRI',
      allocatedNri: 'AllocatedNRI',
      lateralLength: 'LateralLength',
      operator: 'ENVOperator',
      wellStatus: 'ENVWellStatus',
      formation: 'ENVInterval',
      county: 'County',
      dsuName: 'DSUName',
      dealName: 'DealName',
      trajectory: 'Trajectory',
      vintage: 'Vintage',
      displayName: 'WellName'
    },
    visible: true,
    defaultVisible: true,
    geometryType: 'LineString',
    style: {
      color: '#e74c3c',
      weight: 3
    },
    popupTemplate: {
      title: 'Well: {WellName}',
      content: [
        { fieldName: 'WellName', label: 'Well Name' },
        { fieldName: 'API', label: 'API Number' },
        { fieldName: 'ENVOperator', label: 'Operator' },
        { fieldName: 'ENVWellStatus', label: 'Status' },
        { fieldName: 'ENVInterval', label: 'Formation' },
        { fieldName: 'County', label: 'County' },
        { fieldName: 'LateralLength', label: 'Lateral Length (ft)' },
        { fieldName: 'ActiveNRI', label: 'Active NRI' },
        { fieldName: 'DSUName', label: 'DSU Name' },
        { fieldName: 'Trajectory', label: 'Trajectory' },
        { fieldName: 'Vintage', label: 'Vintage' }
      ]
    }
  },

  // LANDGRID LAYERS (Outline Only)
  blocks: {
    name: 'Blocks (Landgrid)',
    url: 'http://localhost:5001/api/geojson/blocks',
    geojsonFile: 'blocks.geojson',
    fields: {
      id: 'OBJECTID',
      blockNumber: 'SURVEYBLO3',
      county: 'CountyName',
      survey: 'SURVEYNAME',
      displayName: 'SURVEYBLO3'
    },
    visible: false,
    defaultVisible: false,
    outlineOnly: true,
    style: {
      fillColor: 'transparent',
      fillOpacity: 0,
      color: '#95a5a6',
      weight: 1,
      dashArray: '5, 5'
    },
    labelField: 'SURVEYBLO3'
  },

  sections: {
    name: 'Sections (Survey)',
    url: 'http://localhost:5001/api/geojson/sections',
    geojsonFile: 'sections.geojson',
    fields: {
      id: 'OBJECTID',
      blkSec: 'BlkSec',
      county: 'County',
      section: 'SURVEYSEC4',
      displayName: 'BlkSec'
    },
    visible: false,
    defaultVisible: false,
    outlineOnly: true,
    style: {
      fillColor: 'transparent',
      fillOpacity: 0,
      color: '#7f8c8d',
      weight: 0.5
    },
    labelField: 'BlkSec'
  },

  // ADDITIONAL LAYERS
  contracts: {
    name: layerNames.contracts,
    url: 'http://localhost:5001/api/geojson/contracts',
    geojsonFile: 'tp-contracts.geojson',
    fields: {
      id: 'OBJECTID',
      tractType: 'TractType',
      tractId: 'TractID',
      county: 'County',
      contractType: 'ContractType',
      contractCat: 'ContractCat',
      status: 'Status',
      grantee: 'Grantee',
      rate: 'Rate',
      effectiveDate: 'EffectiveDate',
      term: 'Term',
      displayName: 'TractID'
    },
    visible: false,
    style: {
      fillColor: '#9b59b6',
      fillOpacity: 0.2,
      color: '#8e44ad',
      weight: 1
    },
    popupTemplate: {
      title: 'Contract: {TractID}',
      content: [
        { fieldName: 'TractID', label: 'Tract ID' },
        { fieldName: 'ContractType', label: 'Contract Type' },
        { fieldName: 'ContractCat', label: 'Category' },
        { fieldName: 'Status', label: 'Status' },
        { fieldName: 'Grantee', label: 'Grantee' },
        { fieldName: 'Rate', label: 'Rate' },
        { fieldName: 'EffectiveDate', label: 'Effective Date' },
        { fieldName: 'Term', label: 'Term' }
      ]
    }
  },

  dsus: {
    name: layerNames.dsus,
    url: 'http://localhost:5001/api/geojson/dsus',
    geojsonFile: 'dsus.geojson',
    visible: false,
    style: {
      fillColor: '#27ae60',
      fillOpacity: 0.2,
      color: '#1e8449',
      weight: 2
    }
  },

  activeRigs: {
    name: layerNames.rigs,
    url: 'http://localhost:5001/api/geojson/rigs',
    geojsonFile: 'env-active-rigs.geojson',
    visible: false,
    style: {
      color: '#f39c12',
      weight: 4
    }
  }
};

/**
 * Query configuration
 */
export const queryConfig = {
  maxResults: parseInt(process.env.REACT_APP_MAX_QUERY_RESULTS || '1000'),
  outFields: ['*'],
  returnGeometry: true,
  spatialReference: { wkid: 4326 }
};

/**
 * Symbology Configuration - Color schemes for attribute-driven styling
 * This allows dynamic coloring based on feature properties
 */
export const symbologyConfig = {
  // Tract Type Colors (TractType field)
  tractTypes: {
    'F': { color: '#2563eb', fillColor: '#3b82f6', label: 'Fee (Ownership)' },        // Blue - primary ownership
    'O': { color: '#7c3aed', fillColor: '#8b5cf6', label: 'ORRI (Royalty)' },         // Purple - overriding royalty
    'N': { color: '#059669', fillColor: '#10b981', label: 'Non-Participating' },     // Green - non-participating
    'U': { color: '#6b7280', fillColor: '#9ca3af', label: 'Undivided' },              // Gray - undivided
    'Q': { color: '#0891b2', fillColor: '#06b6d4', label: 'Qualified' },              // Cyan - qualified
    'V': { color: '#ea580c', fillColor: '#f97316', label: 'Vendor' },                 // Orange - vendor
    // Compound types (inherit primary)
    'OA': { color: '#7c3aed', fillColor: '#8b5cf6', label: 'ORRI-A' },
    'RF': { color: '#2563eb', fillColor: '#3b82f6', label: 'Royalty Fee' },
    'RC': { color: '#7c3aed', fillColor: '#8b5cf6', label: 'Royalty Contract' },
    // Additional tract types for comprehensive coverage
    'TE': { color: '#ca8a04', fillColor: '#eab308', label: 'Term Easement' },         // Yellow - term easement
    'TM': { color: '#0d9488', fillColor: '#14b8a6', label: 'Term Mineral' },          // Teal - term mineral
    'FC': { color: '#4f46e5', fillColor: '#6366f1', label: 'Fee Contract' },          // Indigo - fee contract
    'TC': { color: '#9333ea', fillColor: '#a855f7', label: 'Term Contract' },         // Purple - term contract
    'TO': { color: '#c026d3', fillColor: '#d946ef', label: 'Term Override' },         // Fuchsia - term override
    'RN': { color: '#16a34a', fillColor: '#22c55e', label: 'Royalty Non-Participating' }, // Green
    'MC': { color: '#be185d', fillColor: '#ec4899', label: 'Mineral Contract' },     // Pink - mineral contract
    'UF': { color: '#525252', fillColor: '#737373', label: 'Undivided Fee' },         // Neutral gray
    'PL': { color: '#854d0e', fillColor: '#a16207', label: 'Pipeline' },              // Brown - pipeline
    'WR': { color: '#1d4ed8', fillColor: '#2563eb', label: 'Water Rights' },          // Dark blue
    'RD': { color: '#dc2626', fillColor: '#ef4444', label: 'Road Easement' },         // Red - road
    'QD': { color: '#0e7490', fillColor: '#0891b2', label: 'Qualified Deed' },        // Cyan
    'MF': { color: '#4338ca', fillColor: '#4f46e5', label: 'Mineral Fee' },           // Indigo
    'FF': { color: '#1e40af', fillColor: '#2563eb', label: 'Full Fee' },              // Blue
    'FD': { color: '#3730a3', fillColor: '#4338ca', label: 'Fee Deed' },              // Dark indigo
    'QA': { color: '#0284c7', fillColor: '#0ea5e9', label: 'Qualified-A' },           // Sky blue
    'default': { color: '#64748b', fillColor: '#94a3b8', label: 'Other' }            // Slate - default
  },

  // Well Status Colors (ENVWellStatus field)
  wellStatuses: {
    'PRODUCING': { color: '#16a34a', label: 'Producing' },                            // Green - active production
    'DUC': { color: '#eab308', label: 'DUC (Drilled Uncompleted)' },                  // Yellow - drilled but not completed
    'DRILLING': { color: '#dc2626', label: 'Drilling' },                              // Red - active drilling
    'PERMITTED': { color: '#2563eb', label: 'Permitted' },                            // Blue - permitted
    'COMPLETED': { color: '#0891b2', label: 'Completed' },                            // Cyan - completed
    'INACTIVE PRODUCER': { color: '#6b7280', label: 'Inactive Producer' },           // Gray - inactive
    'DRILLED': { color: '#f97316', label: 'Drilled' },                                // Orange - drilled
    'PERMIT EXPIRED': { color: '#9ca3af', label: 'Permit Expired' },                 // Light gray
    'PERMIT CANCELLED': { color: '#d1d5db', label: 'Permit Cancelled' },             // Lighter gray
    'P & A': { color: '#1f2937', label: 'Plugged & Abandoned' },                     // Dark gray - P&A
    'SHUT-IN': { color: '#78716c', label: 'Shut-in' },                                // Stone - shut in
    'SPUD DATE ONLY': { color: '#a855f7', label: 'Spud Date Only' },                   // Purple - spud only
    'INACTIVE COMPLETED': { color: '#64748b', label: 'Inactive Completed' },          // Slate - inactive but completed
    'TA': { color: '#44403c', label: 'Temporarily Abandoned' },                        // Dark stone - temp abandoned
    'default': { color: '#e74c3c', label: 'Other' }                                   // Default red
  },

  // Helper function to get tract style by type
  getTractStyle: function(tractType) {
    const config = this.tractTypes[tractType] || this.tractTypes['default'];
    return {
      color: config.color,
      fillColor: config.fillColor,
      fillOpacity: 0.4,
      weight: 2
    };
  },

  // Helper function to get well style by status
  getWellStyle: function(wellStatus) {
    const config = this.wellStatuses[wellStatus] || this.wellStatuses['default'];
    return {
      color: config.color,
      weight: 3
    };
  }
};

/**
 * Field mappings for natural language processing
 * Maps common terms to actual field names
 */
export const fieldMappings = {
  // Layer identifiers
  layers: {
    'tract': 'tracts',
    'tracts': 'tracts',
    'asset': 'tracts',
    'assets': 'tracts',
    'land': 'tracts',
    'well': 'wells',
    'wells': 'wells',
    'royalty well': 'wells',
    'royalty wells': 'wells',
    'block': 'blocks',
    'blocks': 'blocks',
    'section': 'sections',
    'sections': 'sections',
    'contract': 'contracts',
    'contracts': 'contracts',
    'easement': 'contracts',
    'easements': 'contracts',
    'pipeline': 'contracts',
    'dsu': 'dsus',
    'dsus': 'dsus',
    'development unit': 'dsus',
    'rig': 'activeRigs',
    'rigs': 'activeRigs',
    'active rig': 'activeRigs',
    'active rigs': 'activeRigs'
  },

  // Field name synonyms - TPL specific
  fields: {
    // Tract fields
    'tract id': 'TractID',
    'tractid': 'TractID',
    'tract number': 'TractID',
    'county': 'CountyName',
    'acreage': 'TractAcres',
    'acres': 'TractAcres',
    'tract acres': 'TractAcres',
    'orri': 'ORRI',
    'override': 'ORRI',
    'override royalty': 'ORRI',
    'nri': 'NRI',
    'net royalty interest': 'NRI',
    'nma': 'NMA',
    'net mineral acres': 'NMA',
    'nra': 'NRA',
    'net royalty acres': 'NRA',
    'block': 'Block',
    'section': 'Sec',
    'deal': 'DealName',
    'deal name': 'DealName',
    'grantee': 'Grantee',
    'status': 'Status',

    // Well fields
    'api': 'API',
    'api number': 'API',
    'well name': 'WellName',
    'operator': 'ENVOperator',
    'well status': 'ENVWellStatus',
    'formation': 'ENVInterval',
    'interval': 'ENVInterval',
    'lateral length': 'LateralLength',
    'active nri': 'ActiveNRI',
    'dsu': 'DSUName',
    'dsu name': 'DSUName',
    'trajectory': 'Trajectory',
    'vintage': 'Vintage',

    // Contract fields
    'contract type': 'ContractType',
    'contract category': 'ContractCat',
    'rate': 'Rate',
    'term': 'Term',
    'effective date': 'EffectiveDate',

    // Block/Section fields
    'block number': 'SURVEYBLO3',
    'survey': 'SURVEYNAME',
    'blksec': 'BlkSec'
  },

  // Common operators for fuzzy matching
  operators: [
    'Exxon', 'ExxonMobil',
    'Diamondback', 'Diamondback Energy',
    'Occidental', 'Oxy',
    'Ovintiv',
    'APA Corp', 'Apache',
    'EOG', 'EOG Resources',
    'Mewbourne', 'Mewbourne Oil',
    'Permian Resources',
    'ConocoPhillips', 'COP',
    'Devon', 'Devon Energy'
  ],

  // Status value mappings
  statusValues: {
    'producing': 'PRODUCING',
    'duc': 'DUC',
    'drilled uncompleted': 'DUC',
    'drilling': 'DRILLING',
    'permitted': 'PERMITTED',
    'shut-in': 'SHUT-IN',
    'shut in': 'SHUT-IN',
    'p&a': 'P&A',
    'plugged': 'P&A',
    'active': 'Active',
    'expired': 'Expired',
    'pending': 'Pending'
  },

  // Counties in TPL operating area
  counties: {
    // Texas
    'reeves': 'REEVES',
    'culberson': 'CULBERSON',
    'loving': 'LOVING',
    'martin': 'MARTIN',
    'midland': 'MIDLAND',
    'ector': 'ECTOR',
    'ward': 'WARD',
    'winkler': 'WINKLER',
    'andrews': 'ANDREWS',
    'pecos': 'PECOS',
    'crane': 'CRANE',
    'howard': 'HOWARD',
    'glasscock': 'GLASSCOCK',
    'reagan': 'REAGAN',
    // New Mexico
    'lea': 'LEA',
    'eddy': 'EDDY',
    'chaves': 'CHAVES'
  },

  // Formations
  formations: {
    'wolfcamp': 'WOLFCAMP',
    'wolfcamp a': 'WOLFCAMP A',
    'wolfcamp b': 'WOLFCAMP B',
    'wolfcamp d': 'WOLFCAMP D',
    'bone spring': 'BONE SPRING',
    'avalon': 'AVALON',
    'delaware': 'DELAWARE',
    'spraberry': 'SPRABERRY'
  }
};

export default {
  mapConfig,
  arcgisConfig,
  featureServices,
  queryConfig,
  fieldMappings
};
